import React, { useState } from 'react';
import { Info, X } from 'lucide-react';
import { shouldUseGobodo } from '../../utils/gobodoUtils';

const isProduction = () =>
  typeof process !== 'undefined' && process.env?.NODE_ENV === 'production';

interface DevModeBannerProps {
  /**
   * The data source/query file being used
   * Example: "objectQueries.ts", "fieldQueries.ts"
   */
  dataSource: string;

  /**
   * Optional: Specific details about what's mocked
   * Example: "5 objects", "23 field types", "3 page layouts"
   */
  itemCount?: string;

  /**
   * Optional: Allow dismissing the banner for this session
   * Default: false (always show)
   */
  dismissible?: boolean;

  /**
   * Optional: Custom message override
   */
  customMessage?: string;
}

export const DevModeBanner: React.FC<DevModeBannerProps> = ({
  dataSource,
  itemCount,
  dismissible = false,
  customMessage
}) => {
  const [dismissed, setDismissed] = useState(false);

  if (isProduction() || shouldUseGobodo()) {
    return null;
  }

  const storageKey = `dev-banner-dismissed-${dataSource}`;
  const isDismissedInSession = dismissible && sessionStorage.getItem(storageKey) === 'true';

  if (dismissed || isDismissedInSession) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    if (dismissible) {
      sessionStorage.setItem(storageKey, 'true');
    }
  };

  const defaultMessage = customMessage ||
    `Development Mode: ${itemCount ? `Showing ${itemCount} from` : 'Using'} mock data in ${dataSource}. Connect backend API to persist changes.`;

  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
      <div className="flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            {defaultMessage}
          </p>
        </div>
        {dismissible && (
          <button
            onClick={handleDismiss}
            className="p-1 hover:bg-blue-100 dark:hover:bg-blue-800 rounded transition-colors"
            title="Dismiss for this session"
          >
            <X className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </button>
        )}
      </div>
    </div>
  );
};
